"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

type GlassCardProps = {
  title?: string;
  description?: ReactNode;
  icon?: ReactNode;
  imageSrc?: string;
  imageAlt?: string;
  animationSlot?: ReactNode;
  href?: string;
  children?: ReactNode;
  className?: string;
};

export function GlassCard({
  title,
  description,
  icon,
  imageSrc,
  imageAlt,
  animationSlot,
  href,
  children,
  className,
}: GlassCardProps) {
  const { t } = useTranslation();

  return (
    <div className={`group relative p-6 rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 ease-out h-full flex flex-col justify-between hover:-translate-y-2 shadow-[0_20px_60px_rgba(0,255,255,0.08)] ${className ?? ""}`}>
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
      <div className="flex flex-col">
        {(icon || title || description) && (
          <div className="flex items-start gap-4 mb-3">
            {icon && (
              <div className="flex-shrink-0 mt-1 w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-400/20 to-fuchsia-400/20 flex items-center justify-center">
                <div className="text-white">{icon}</div>
              </div>
            )}
            {(title || description) && (
              <div>
                {title && <h3 className="text-xl mb-2">{title}</h3>}
                {description && (
                  <div className="text-white/70 leading-relaxed">{description}</div>
                )}
              </div>
            )}
          </div>
        )}
        {animationSlot && (
          <div className="mt-4 w-full rounded-lg overflow-hidden">
            {animationSlot}
          </div>
        )}
        {!animationSlot && imageSrc && (
          <div className="mt-4 w-full rounded-lg border border-white/10 /50">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={imageSrc} alt={imageAlt || title || "image"} className="w-full h-auto object-contain mx-auto block" />
          </div>
        )}
        {children}
      </div>
      {href && (
        <div className="mt-3 text-right">
          <Link href={href} className="inline-flex items-center text-sm text-cyan-300 hover:text-cyan-200 transition-colors">
            <span>{t("landing.try_it_now", "Try it now")}</span>
            <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      )}
    </div>
  );
}
